import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { Module, Store } from "@apis/index";
import Typography from "@components/typography";
import Row from "@components/row";
import * as S from "../style";
import { Formik, Form } from "formik";
import DropDown from "@components/fields/dropdown";
import ModuleDropdown from "@commons/modules/dropdown";
import useConfig from "@hooks/useConfig";

interface StoreSelectorProps {
  onStoreSelect: (storeId: number | null) => void;
  selectedStoreId: number | null;
}

const StoreSelector: React.FC<StoreSelectorProps> = ({
  onStoreSelect,
  selectedStoreId,
}) => {
  const { bucketUrl } = useConfig();
  const { items: stores, loading: storesLoading } = useSelector(
    (state: RootState) => state.stores,
  );

  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);

  useEffect(() => {
    Module.list({ state: "modules", params: { isActive: true } });
  }, []);

  useEffect(() => {
    if (selectedModuleId) {
      Store.list({
        state: "stores",
        params: {
          moduleId: selectedModuleId,
          limit: 3000,
        },
      });
    }
  }, [selectedModuleId]);

  // Formating stores options to map avatar, cover image, and initials
  const formattedStores = (stores || []).map((store: any) => {
    const name = store.storeName || store.name || "";
    const initials = name
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .slice(0, 2);

    let imageUrl = "";
    if (store.avatar) {
      imageUrl = `${bucketUrl}/${store.avatar}`;
    } else if (store.coverImg) {
      imageUrl = `${bucketUrl}/${store.coverImg}`;
    } else if (Array.isArray(store.coverImages) && store.coverImages.length > 0) {
      const firstCover = store.coverImages[0];
      const coverPath = typeof firstCover === "string" ? firstCover : firstCover.coverImages || firstCover.image;
      if (coverPath) {
        imageUrl = `${bucketUrl}/${coverPath}`;
      }
    }

    return {
      ...store,
      image: imageUrl || null,
      initials: initials || "",
    };
  });

  return (
    <S.Card style={{ marginBottom: "20px" }}>
      <Typography styleText="font-weight: 600; margin-bottom: 12px;">
        Select Store
      </Typography>
      <Formik
        initialValues={{
          moduleId: selectedModuleId || "",
          storeId: selectedStoreId || "",
        }}
        enableReinitialize
        onSubmit={() => {}}
      >
        {({ setFieldValue }) => (
          <Form>
            <Row gap="12px">
              <div style={{ flex: 1 }}>
                <ModuleDropdown
                  name="moduleId"
                  label="Module"
                  onChange={(val) => {
                    setFieldValue("moduleId", val);
                    setFieldValue("storeId", "");
                    setSelectedModuleId(Number(val));
                    onStoreSelect(null);
                  }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <DropDown
                  name="storeId"
                  label="Store"
                  options={formattedStores}
                  titleAsKey="storeName"
                  valueAsKey="id"
                  placeholder={storesLoading ? "Loading..." : "Select Store"}
                  disabled={!selectedModuleId || storesLoading}
                  onChange={(val) => {
                    setFieldValue("storeId", val);
                    onStoreSelect(Number(val));
                  }}
                  hasSearch
                />
              </div>
            </Row>
          </Form>
        )}
      </Formik>
    </S.Card>
  );
};

export default StoreSelector;
